/**
 * Email Campaign Scheduled Page — /marketing/campaigns/:id/scheduled
 *
 * Read-only view of a campaign that is waiting to send. Nothing here edits the
 * campaign: a scheduled campaign fires unattended, so an edit made "just for a
 * second" can ship with a mistake before anyone can undo it. The only action is
 * Cancel Send, which drops the campaign back to draft and returns to the editor.
 *
 * The editor, the contacts/excludes sub-pages and the list page all route a
 * scheduled campaign here instead of into the editor.
 */
import { useEffect, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, Clock, Loader2, Lock, Users } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatWallClockInTz } from '@/lib/facilityTimezone';
import * as campaignsSvc from '@/services/campaigns.service';

// The API has already converted `scheduledAt` into the account's zone and
// returns it without an offset, so it must be formatted as-is rather than
// re-read through the browser's zone. See `formatWallClockInTz`.
function formatScheduleDate(iso: string, tz: string): string {
  return formatWallClockInTz(iso, tz, { weekday: true });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="block text-sm font-medium text-gray-600 mb-1">{label}</div>
      <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 break-words min-h-[2.375rem]">
        {value ?? <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
}

export default function EmailCampaignScheduledPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const userTz = useUserTimezone();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-editor', id],
    queryFn: () => campaignsSvc.getCampaign(campaignId),
    enabled: !!id,
  });

  // Only a scheduled campaign belongs here. Drafts go back to the editor;
  // anything already sent (or sending) goes to its stats.
  useEffect(() => {
    if (!campaign) return;
    if (campaign.status === 'draft') navigate(`/marketing/campaigns/${id}`, { replace: true });
    else if (campaign.status !== 'scheduled') navigate(`/marketing/campaigns/${id}/stats`, { replace: true });
  }, [campaign, id, navigate]);

  const isScheduled = campaign?.status === 'scheduled';

  const { data: attachedLists = [] } = useQuery({
    queryKey: ['campaign-lists', id],
    queryFn: () => campaignsSvc.getCampaignLists(campaignId),
    enabled: isScheduled,
  });
  const { data: contactCount = 0 } = useQuery({
    queryKey: ['campaign-contact-count', id],
    queryFn: () => campaignsSvc.getCampaignContactCount(campaignId),
    enabled: isScheduled,
  });
  const { data: excludeCount = 0 } = useQuery({
    queryKey: ['campaign-exclude-count', id],
    queryFn: () => campaignsSvc.getCampaignExcludeCount(campaignId),
    enabled: isScheduled,
  });
  const { data: recipientCount, isLoading: recipientLoading } = useQuery({
    queryKey: ['campaign-recipient-count', id],
    queryFn: () => campaignsSvc.getRecipientCount(campaignId),
    enabled: isScheduled,
    staleTime: 0,
  });

  const cancelMut = useMutation({
    mutationFn: () => campaignsSvc.cancelSchedule(campaignId),
    onSuccess: () => {
      toast.success('Send cancelled — the campaign is a draft again and can be edited');
      qc.invalidateQueries({ queryKey: ['campaign-editor', id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns-stats'] });
      navigate(`/marketing/campaigns/${id}`, { replace: true });
    },
    onError: () => toast.error('Failed to cancel the scheduled send'),
  });

  /**
   * srcdoc has no document URL, so relative image paths in the email would not
   * resolve. A <base> restores them (same trick as the editor's review modal).
   */
  const previewDoc = useMemo(() => {
    const html = campaign?.emailBody ?? '';
    if (!html) return '';
    const base = `<base href="${window.location.origin}/">`;
    if (/<head[^>]*>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
    if (/<html[^>]*>/i.test(html)) return html.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
    return `<!DOCTYPE html><html><head>${base}</head><body style="margin:0">${html}</body></html>`;
  }, [campaign?.emailBody]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <p className="text-slate-500">Campaign not found.</p>
        <button type="button" onClick={() => navigate('/marketing/email-campaigns')} className="text-sm text-blue-600 hover:underline">
          Back to Campaigns
        </button>
      </div>
    );
  }

  // Redirecting (see effect above) — render nothing rather than a flash of details.
  if (!isScheduled) return null;

  const excludesAccountUsers = !!(campaign as any).accountUsers;
  const fromLine = campaign.fromEmail
    ? campaign.fromName ? `${campaign.fromName} <${campaign.fromEmail}>` : campaign.fromEmail
    : null;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      {/* ── Header bar ── */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={() => navigate('/marketing/email-campaigns')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h1 className="text-base font-semibold text-gray-800 truncate max-w-md">
            Scheduled Campaign: {campaign.name || 'Untitled'}
          </h1>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-[#fff3cd] text-[#856404] border border-[#ffc107]">
            <Clock className="h-3 w-3" />
            Scheduled
          </span>
        </div>

        <button
          type="button"
          onClick={() => cancelMut.mutate()}
          disabled={cancelMut.isPending}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60"
          style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffc107' }}
        >
          <Ban className="h-4 w-4" />
          {cancelMut.isPending ? 'Cancelling…' : 'Cancel Send'}
        </button>
      </div>

      {/* ── Locked banner ── */}
      <div className="mx-4 mt-3 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <div>
            This campaign is scheduled to send on{' '}
            <strong>{campaign.scheduledAt ? formatScheduleDate(campaign.scheduledAt, userTz) : '—'}</strong>
            {' '}and is locked until then.
          </div>
          <div className="mt-0.5 text-amber-700">
            To change anything, use <strong>Cancel Send</strong>, edit and save the draft, then schedule it again.
          </div>
        </div>
      </div>

      {/* ── Body: details + recipients | preview ── */}
      <div className="flex flex-1 min-h-0 mt-3">
        {/* Left: details + contacts (mirrors the editor's left panel, read-only) */}
        <div className="w-[42%] md:min-w-[400px] max-w-[560px] overflow-y-auto border-r border-gray-200 bg-white">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Email Campaign Details
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-blue-500" />
              {recipientLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
              ) : (
                <span className="text-lg font-bold text-blue-600">
                  {(recipientCount ?? 0).toLocaleString()}
                </span>
              )}
              <span className="text-gray-500">contacts</span>
            </div>
          </div>

          <div className="p-5 space-y-5">
            <DetailRow label="Campaign Name" value={campaign.name || 'Untitled'} />
            <DetailRow label="Description" value={campaign.description} />
            <DetailRow label="Subject Line" value={campaign.subject} />
            <DetailRow label="Preheader" value={campaign.preheader} />
            <DetailRow label="From" value={fromLine} />
            <DetailRow
              label="Schedule Send"
              value={campaign.scheduledAt ? <span className="font-medium text-gray-800">{formatScheduleDate(campaign.scheduledAt, userTz)}</span> : null}
            />
            {/* Only worth calling out when it changes who receives the send. */}
            {excludesAccountUsers && (
              <DetailRow label="Account Users" value="Excluded from this campaign" />
            )}

            {/* ── Campaign Contacts Section ── */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#f0f7ff' }}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold" style={{ color: '#00aff0' }}>{contactCount}</span>
                  <span className="text-sm text-gray-600">direct contact(s)</span>
                </div>
                <Link to={`/marketing/campaigns/${id}/contacts`} className="text-sm font-medium text-blue-600 hover:underline">
                  View
                </Link>
              </div>

              <div className="px-4 py-3 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl font-bold text-indigo-500">{attachedLists.length}</span>
                  <span className="text-sm text-gray-600">contact {attachedLists.length === 1 ? 'list' : 'lists'}</span>
                </div>
                {attachedLists.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No lists attached</p>
                ) : (
                  <div className="space-y-1.5">
                    {attachedLists.map(l => (
                      <div key={l.emailListId} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-gray-700 truncate">{l.name}</span>
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={l.listType === 'dynamic'
                              ? { background: '#fff3e0', color: '#e65100' }
                              : { background: '#e3f2fd', color: '#1565c0' }
                            }>
                            {l.listType}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">{(l.uniqueEmailCount ?? l.patientCount).toLocaleString()} emails</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between" style={{ background: '#fff8f8' }}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-red-500">{excludeCount}</span>
                  <span className="text-sm text-gray-600">excluded contact(s)</span>
                </div>
                <Link to={`/marketing/campaigns/${id}/excludes`} className="text-sm font-medium text-blue-600 hover:underline">
                  View
                </Link>
              </div>
            </div>
            <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
              Direct contacts and list members are merged at send time. Excluded contacts and account users (if excluded) are removed before sending.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 min-w-0 bg-[#1e1e2e] flex flex-col">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Email Preview</div>
          {previewDoc ? (
            <iframe
              title="Email preview"
              srcDoc={previewDoc}
              sandbox=""
              className="flex-1 w-full border-0 bg-white"
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              This campaign has no email content.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
